/**
 * Branding runtime — applique un branding tenant aux CSS variables `:root`.
 *
 * Source unique de vérité pour la propagation des couleurs/logo de marque
 * dans le DOM. Utilisé à 2 endroits :
 *   1. au boot, depuis AuthContext, après `loadProfile()` → applyBranding(tenant.branding)
 *   2. live preview, depuis la page Settings → applyBranding(formState) à chaque changement
 *
 * Comportement :
 *   - applyBranding(null|undefined)  → reset complet (retour aux valeurs initiales
 *                                       du DOM, capturées au premier appel).
 *   - applyBranding(partial)         → set uniquement les clés présentes ; les
 *                                       autres reviennent à leur valeur initiale.
 *
 * On ne dépend pas d'un fichier `theme.json` : la valeur initiale est lue
 * une seule fois depuis `getComputedStyle(document.documentElement)`. Cela
 * garantit qu'un reset reproduit fidèlement ce qui est défini en `:root`
 * dans `index.css` — pas de duplication, pas de drift.
 *
 * `logo_url` est un cas à part — pas une CSS var. Un composant brand custom
 * peut lire `tenant.branding.logo_url` directement via le contexte ; ce helper
 * ne touche QUE les variables CSS de couleur.
 *
 * Note typage : on définit `Branding` localement plutôt que d'importer
 * `Schemas.BrandingInput` du shared, pour deux raisons :
 *   1. shared ne l'expose qu'à travers le namespace `Schemas` (pas en
 *      top-level) — un `import { BrandingInput }` ne compile pas.
 *   2. ce helper ne valide rien : il accepte un input partiel best-effort
 *      et ignore silencieusement les valeurs invalides. La validation
 *      stricte (Zod) se fait au PATCH côté API.
 */

/** 6 couleurs hex + logo URL. Aligné sur BrandingSchema (shared). */
export type Branding = {
  primary?: string;
  primary_foreground?: string;
  bg?: string;
  surface?: string;
  border?: string;
  text?: string;
  logo_url?: string;
};

/** Mapping clé branding (couleurs uniquement) → CSS variable. */
const BRANDING_TO_CSS_VAR = {
  primary: "--brand-primary",
  primary_foreground: "--brand-primary-foreground",
  bg: "--brand-bg",
  surface: "--brand-surface",
  border: "--brand-border",
  text: "--brand-text",
} as const satisfies Record<string, string>;

type ColorKey = keyof typeof BRANDING_TO_CSS_VAR;

const COLOR_KEYS = Object.keys(BRANDING_TO_CSS_VAR) as ColorKey[];

/** Snapshot des valeurs `:root` au premier `applyBranding`. Lazy. */
let initialValues: Record<ColorKey, string> | null = null;

function captureInitial(root: HTMLElement): Record<ColorKey, string> {
  if (initialValues) return initialValues;
  const computed = getComputedStyle(root);
  const snapshot = {} as Record<ColorKey, string>;
  for (const key of COLOR_KEYS) {
    const cssVar = BRANDING_TO_CSS_VAR[key];
    snapshot[key] = computed.getPropertyValue(cssVar).trim();
  }
  initialValues = snapshot;
  return snapshot;
}

/**
 * Applique un branding au document root.
 *
 * @param branding   Branding tenant. `null`/`undefined` → reset complet.
 * @param root       Pour les tests. Default: `document.documentElement`.
 */
export function applyBranding(
  branding: Branding | null | undefined,
  root: HTMLElement = document.documentElement,
): void {
  const initial = captureInitial(root);

  if (!branding) {
    // Reset complet — ré-applique toutes les valeurs initiales.
    for (const key of COLOR_KEYS) {
      const cssVar = BRANDING_TO_CSS_VAR[key];
      root.style.setProperty(cssVar, initial[key]);
    }
    return;
  }

  // Partial : pour chaque clé, soit on applique la valeur fournie, soit
  // on retombe sur la valeur initiale. Garantit l'absence de "fantôme" entre
  // deux saves successifs (ex. j'ai changé primary, puis je clear primary,
  // ça doit revenir à initial — pas rester sur l'ancien primary).
  for (const key of COLOR_KEYS) {
    const cssVar = BRANDING_TO_CSS_VAR[key];
    const value = branding[key];
    if (typeof value === "string" && value.length > 0) {
      root.style.setProperty(cssVar, value);
    } else {
      root.style.setProperty(cssVar, initial[key]);
    }
  }
}

/**
 * Test-only — réinitialise le snapshot des valeurs initiales.
 * Permet de simuler un rechargement de page entre deux tests.
 */
export function __resetBrandingForTests(): void {
  initialValues = null;
}

/**
 * Test-only — pré-amorce le snapshot initial sans passer par le DOM.
 * Utilisé en environnement node (sans jsdom) où `getComputedStyle` est
 * indisponible. En production (boot navigateur), `captureInitial` lit le
 * DOM réel ; on n'utilise jamais cette fonction.
 */
export function __seedInitialForTests(values: Record<ColorKey, string>): void {
  initialValues = { ...values };
}
